import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Geometry, Point } from 'ol/geom';
import { Fill, RegularShape, Stroke, Style } from 'ol/style';
import { Feature } from 'ol';
import { fromLonLat } from 'ol/proj';
import { VoyageRoutingModel } from '../models/voyage-routing-model';
import { VesselPlanningModel, VoyagePlan } from '../models/vessel-planning-model';
import { UiStateModel, UiStateModelParam } from '../models/ui-state-model';
import { SuggestedRouteEvents } from '../../local_modules/voyage-suggested-routes/src/types';
import { FloatPanelComponent } from '../../vessel/src/main-app';
import { extractShallowSoundings } from '../utils/route-utils';

const _PROJECTION = (<any>window)["appenv"].projection;

const _DEFAULT_SOUNDING_STYLE = new Style({
    image: new RegularShape({
        points: 3,
        radius: 9,
        fill: new Fill({ color: '#E37625' }),
        stroke: new Stroke({ color: '#FFFFFF', width: 1.5 }),
    }),
    zIndex: 20,
});

const _SELECTED_SOUNDING_STYLE = new Style({
    image: new RegularShape({
        points: 3,
        radius: 11,
        fill: new Fill({ color: '#E37625' }),
        stroke: new Stroke({ color: '#2451DC', width: 3 }),
    }),
    zIndex: 21,
});

export class ShallowSoundingsLayer extends EventTarget
{
    static readonly layerId = 'ShallowSoundingsLayer';

    private _layer: VectorLayer<VectorSource<Geometry>> | null = null;
    private _vectorSource: VectorSource<Geometry> | null = null;
    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    // enable() can be called before initialize() (feature flags resolve asynchronously),
    // so remember the intent and honor it once the layer exists
    private _enabled: boolean = false;
    // Bumped on every _onSelectedRouteChanged() call and on disable(); lets a stale,
    // slow-resolving call detect that a newer one (or a disable()) has since taken
    // over, so it discards its result instead of repopulating the map with a
    // previously-selected route's soundings.
    private _updateToken: number = 0;
    // Coords of the sounding last chosen from the alert popup (view-shallow-sounding-on-map),
    // so its triangle marker can be rendered highlighted on the map.
    private _selectedCoords: [number, number] | null = null;

    constructor()
    {
        super();
        this._onSelectedRouteChanged = this._onSelectedRouteChanged.bind(this);
        this._onSoundingSelected = this._onSoundingSelected.bind(this);
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        this._vectorSource = new VectorSource();
        this._layer = new VectorLayer({ source: this._vectorSource, visible: this._enabled });
        this._layers.push(this._layer);

        VesselPlanningModel!.addEventListener("executing-plan-changed", this._onSelectedRouteChanged);
        VesselPlanningModel!.addEventListener(SuggestedRouteEvents.PlanChanged, this._onSelectedRouteChanged);
        VesselPlanningModel!.addEventListener("view-shallow-sounding-on-map", <EventListener>this._onSoundingSelected);

        if (this._enabled) {
            this._onSelectedRouteChanged();
        }
    }

    enable()
    {
        this._enabled = true;
        if (!this._layer) {
            return;
        }
        this._layer.setVisible(true);
        this._onSelectedRouteChanged();
    }

    disable()
    {
        this._enabled = false;
        this._updateToken++;
        if (!this._layer) {
            return;
        }
        this._layer.setVisible(false);
        this._clear();
    }

    private _clear()
    {
        try {
            this._vectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    private async _getSelectedRoute()
    {
        let plan: VoyagePlan | null = null;

        const mode: FloatPanelComponent = <FloatPanelComponent>UiStateModel!.getValue(UiStateModelParam.ApplicationMode);

        if (mode === "suggestedRoutes" || mode === "routeEditor") {
            const activePlan = VoyageRoutingModel!.activePlan;
            if (activePlan) {
                plan = await VesselPlanningModel!.getCurrentPlan(activePlan);
            }
        } else {
            plan = await VesselPlanningModel!.getExecutingPlan();
        }

        if (!plan) {
            return null;
        }

        const selectedRouteIndex = plan.routes.findIndex(route => route.routeId === plan!.currentSelectedRouteId) ?? plan.selectedRouteIndex;
        if (selectedRouteIndex === -1) {
            return null;
        }

        const route = plan.routes[selectedRouteIndex] ?? null;
        return route ? { route, plan } : null;
    }

    private async _onSelectedRouteChanged()
    {
        if (!this._layer!.getVisible()) {
            return;
        }
        const token = ++this._updateToken;
        this._clear();

        const selected = await this._getSelectedRoute();

        // A newer call (or a disable()) started while we were awaiting - its result
        // is authoritative, so discard this stale one instead of repopulating the map.
        if (token !== this._updateToken || !this._layer!.getVisible()) {
            return;
        }

        if (!selected) {
            return;
        }

        const shallowSoundings = extractShallowSoundings(selected.route, selected.plan.foreDraft, selected.plan.aftDraft);
        if (!shallowSoundings.length) {
            return;
        }

        const features: Feature<Geometry>[] = shallowSoundings.map((sounding) => {
            const feature = new Feature({
                geometry: new Point(fromLonLat(sounding.coords, _PROJECTION)),
            });

            const isSelected = this._isSelectedCoords(sounding.coords);
            feature.setStyle(isSelected ? _SELECTED_SOUNDING_STYLE : _DEFAULT_SOUNDING_STYLE);

            feature.set('layer', ShallowSoundingsLayer.layerId);
            feature.set('text', `Shallow sounding — depth ${sounding.depth.toFixed(2)}m, CATZOC ${sounding.catzoc}`);
            feature.set('coords', sounding.coords);
            return feature;
        });

        this._vectorSource!.addFeatures(features);
    }

    private _isSelectedCoords(coords: [number, number]): boolean
    {
        return !!this._selectedCoords && coords[0] === this._selectedCoords[0] && coords[1] === this._selectedCoords[1];
    }

    private _onSoundingSelected(evt: CustomEvent)
    {
        const coords: [number, number] | undefined = evt.detail?.coords;
        if (!coords) {
            return;
        }
        this._selectedCoords = coords;

        for (const feature of this._vectorSource?.getFeatures() ?? []) {
            const featureCoords = feature.get('coords') as [number, number] | undefined;
            const isSelected = !!featureCoords && this._isSelectedCoords(featureCoords);
            feature.setStyle(isSelected ? _SELECTED_SOUNDING_STYLE : _DEFAULT_SOUNDING_STYLE);
        }
    }
};
